if (typeof customElements.get('product-details') === 'undefined') {
  const MAIN_SPECIFICATION_LIMIT = 6;

  class ProductDetails extends HTMLElement {
    constructor() {
      super();
      this._onVariantChange = this._onVariantChange.bind(this);
      this._onAdditionalToggle = this._onAdditionalToggle.bind(this);
      this._onClick = this._onClick.bind(this);
      this._onTooltipPreview = this._onTooltipPreview.bind(this);
      this._onDocumentPointerDown = this._onDocumentPointerDown.bind(this);
      this._onDocumentKeyDown = this._onDocumentKeyDown.bind(this);
      this._onWindowResize = this._onWindowResize.bind(this);
      this._variantTemplates = new Map();
      this._productVariants = null;
      this._additionalWasOpen = false;
      this._currentVariantId = null;
    }

    connectedCallback() {
      this._variantTemplates.clear();
      this.querySelectorAll('template[data-product-details-variant]').forEach((template) => {
        this._variantTemplates.set(template.dataset.productDetailsVariant, template);
      });

      this._content = this.querySelector('[data-product-details-content]');
      this._additionalDetails = this.querySelector('[data-product-details-additional]');
      this._additionalItems = this.querySelector('[data-product-details-additional-items]');
      this._additionalSummary = this.querySelector('[data-product-details-additional-summary]');
      this._currentVariantId = this.dataset.currentVariantId || null;
      this._additionalWasOpen = Boolean(this._additionalDetails?.open);

      this.addEventListener('click', this._onClick);
      this.addEventListener('pointerover', this._onTooltipPreview);
      this.addEventListener('focusin', this._onTooltipPreview);
      document.addEventListener('pointerdown', this._onDocumentPointerDown);
      document.addEventListener('keydown', this._onDocumentKeyDown);
      window.addEventListener('resize', this._onWindowResize);
      this._additionalDetails?.addEventListener('toggle', this._onAdditionalToggle);

      this._organizeSpecifications();
      this._connectToMainProduct();
      window.__keyideasDualProductActions?.reconcile(this);
    }

    disconnectedCallback() {
      this._disconnectFromMainProduct();
      this.removeEventListener('click', this._onClick);
      this.removeEventListener('pointerover', this._onTooltipPreview);
      this.removeEventListener('focusin', this._onTooltipPreview);
      document.removeEventListener('pointerdown', this._onDocumentPointerDown);
      document.removeEventListener('keydown', this._onDocumentKeyDown);
      window.removeEventListener('resize', this._onWindowResize);
      this._additionalDetails?.removeEventListener('toggle', this._onAdditionalToggle);
    }

    async _connectToMainProduct() {
      await customElements.whenDefined('product-variants');
      if (!this.isConnected) return;

      const containingProduct = this.closest('product-page[data-product-id]');
      const mainProduct = containingProduct?.dataset.productId === this.dataset.productId
        ? containingProduct
        : [...document.querySelectorAll('product-page[data-product-id]')].find(
          (productPage) =>
            productPage.dataset.productId === this.dataset.productId &&
            !productPage.hasAttribute('data-quick-view-section-id') &&
            !productPage.closest('#product-quick-view')
        );

      const productVariants = mainProduct?.querySelector('product-variants[data-main-product-page-variants]');
      if (!productVariants) return;

      this._disconnectFromMainProduct();
      this._productVariants = productVariants;
      this._productVariants.addEventListener('VARIANT_CHANGE', this._onVariantChange);

      const currentVariantId = this._productVariants.currentVariant?.id;
      if (currentVariantId && String(currentVariantId) !== this._currentVariantId) {
        this._renderVariant(currentVariantId, false);
      }
    }

    _disconnectFromMainProduct() {
      this._productVariants?.removeEventListener('VARIANT_CHANGE', this._onVariantChange);
      this._productVariants = null;
    }

    _onVariantChange(event) {
      const variant = event.currentTarget.currentVariant;
      if (variant) this._renderVariant(variant.id, true);
    }

    _onAdditionalToggle() {
      this._additionalWasOpen = this._additionalDetails.open;
      if (
        !this._additionalDetails.open &&
        this._additionalDetails.querySelector('[data-product-details-tooltip][data-open="true"]')
      ) {
        this._closeTooltips();
      }
    }

    _onClick(event) {
      const trigger = event.target instanceof Element
        ? event.target.closest('[data-product-details-tooltip-trigger]')
        : null;
      if (!trigger || !this.contains(trigger)) return;

      event.preventDefault();
      const tooltip = trigger.closest('[data-product-details-tooltip]');
      const shouldOpen = tooltip?.dataset.open !== 'true';
      this._closeTooltips();

      if (tooltip && shouldOpen) {
        tooltip.dataset.open = 'true';
        trigger.setAttribute('aria-expanded', 'true');
        this._positionTooltip(tooltip);
      }
    }

    _onTooltipPreview(event) {
      const trigger = event.target instanceof Element
        ? event.target.closest('[data-product-details-tooltip-trigger]')
        : null;
      if (!trigger || !this.contains(trigger)) return;

      const tooltip = trigger.closest('[data-product-details-tooltip]');
      const openTooltip = this.querySelector('[data-product-details-tooltip][data-open="true"]');
      if (openTooltip && openTooltip !== tooltip) {
        const openTrigger = openTooltip.querySelector('[data-product-details-tooltip-trigger]');
        this._closeTooltips();

        if (event.type === 'pointerover' && openTrigger === document.activeElement) {
          openTrigger.blur();
        }
      }

      this._positionTooltip(tooltip);
    }

    _onDocumentPointerDown(event) {
      const openTooltip = this.querySelector('[data-product-details-tooltip][data-open="true"]');
      if (openTooltip && !openTooltip.contains(event.target)) this._closeTooltips();
    }

    _onDocumentKeyDown(event) {
      if (event.key === 'Escape') this._closeTooltips(true);
    }

    _onWindowResize() {
      const openTooltip = this.querySelector('[data-product-details-tooltip][data-open="true"]');
      if (openTooltip) this._positionTooltip(openTooltip);
    }

    _positionTooltip(tooltip) {
      const content = tooltip?.querySelector('[data-product-details-tooltip-content]');
      const trigger = tooltip?.querySelector('[data-product-details-tooltip-trigger]');
      if (!content || !trigger) return;

      content.style.removeProperty('--product-details-tooltip-offset-x');
      content.style.removeProperty('--product-details-tooltip-max-height');
      content.style.removeProperty('--product-details-tooltip-max-width');
      tooltip.removeAttribute('data-placement');

      const viewportMargin = 16;
      const tooltipGap = 10;
      const scrollPane = this.matches('.quick-view-details') && this.closest('[data-quick-view-section-id]')
        ? this.closest('.kt-rich-text__content')
        : null;
      const paneBounds = scrollPane?.getBoundingClientRect();
      const visibleBounds = {
        left: paneBounds ? Math.max(0, paneBounds.left) : 0,
        top: paneBounds ? Math.max(0, paneBounds.top) : 0,
        right: paneBounds ? Math.min(window.innerWidth, paneBounds.right) : window.innerWidth,
        bottom: paneBounds ? Math.min(window.innerHeight, paneBounds.bottom) : window.innerHeight,
      };
      if (paneBounds) {
        content.style.setProperty(
          '--product-details-tooltip-max-width',
          `${Math.max(0, visibleBounds.right - visibleBounds.left - viewportMargin * 2)}px`
        );
      }
      const triggerBounds = trigger.getBoundingClientRect();
      const contentHeight = Math.min(
        content.scrollHeight,
        visibleBounds.bottom - visibleBounds.top - viewportMargin * 2
      );
      const spaceAbove = triggerBounds.top - visibleBounds.top - viewportMargin - tooltipGap;
      const spaceBelow = visibleBounds.bottom - triggerBounds.bottom - viewportMargin - tooltipGap;
      const placeBelow = spaceAbove < contentHeight && spaceBelow > spaceAbove;

      if (placeBelow) {
        tooltip.dataset.placement = 'below';
      }

      const availableHeight = placeBelow ? spaceBelow : spaceAbove;
      content.style.setProperty('--product-details-tooltip-max-height', `${Math.max(paneBounds ? 0 : 80, availableHeight)}px`);

      const bounds = content.getBoundingClientRect();
      let horizontalOffset = 0;
      if (bounds.left < visibleBounds.left + viewportMargin) {
        horizontalOffset = visibleBounds.left + viewportMargin - bounds.left;
      } else if (bounds.right > visibleBounds.right - viewportMargin) {
        horizontalOffset = visibleBounds.right - viewportMargin - bounds.right;
      }

      if (horizontalOffset !== 0) {
        content.style.setProperty('--product-details-tooltip-offset-x', `${horizontalOffset}px`);
      }
    }

    _closeTooltips(returnFocus = false) {
      let focusTarget = null;
      this.querySelectorAll('[data-product-details-tooltip][data-open="true"]').forEach((tooltip) => {
        const trigger = tooltip.querySelector('[data-product-details-tooltip-trigger]');
        const content = tooltip.querySelector('[data-product-details-tooltip-content]');
        tooltip.removeAttribute('data-open');
        tooltip.removeAttribute('data-placement');
        content?.style.removeProperty('--product-details-tooltip-offset-x');
        content?.style.removeProperty('--product-details-tooltip-max-height');
        content?.style.removeProperty('--product-details-tooltip-max-width');
        trigger?.setAttribute('aria-expanded', 'false');
        focusTarget ||= trigger;
      });

      if (returnFocus) focusTarget?.focus();
    }

    _restoreAdditionalItems() {
      if (!this._content || !this._additionalDetails) return;

      const additionalItems = [...this._additionalDetails.querySelectorAll('[data-product-details-item]')].sort(
        (first, second) =>
          Number(first.dataset.productDetailsOrder || 0) - Number(second.dataset.productDetailsOrder || 0)
      );
      additionalItems.forEach((item) => {
        if (item.hasAttribute('data-product-details-duplicate')) {
          item.remove();
        } else {
          this._content.append(item);
        }
      });
      this._additionalItems?.replaceChildren();
    }

    _organizeSpecifications() {
      if (!this._content) return;

      this._restoreAdditionalItems();
      const items = [...this._content.children].filter((item) => item.matches('[data-product-details-item]'));

      if (!this._additionalDetails || !this._additionalItems) {
        this._applyValueLengthClasses(items);
        return;
      }

      items.forEach((item, index) => {
        if (!item.hasAttribute('data-product-details-order')) {
          item.dataset.productDetailsOrder = String(index);
        }
      });

      const itemsByKey = new Map();
      const uniqueItems = items.filter((item) => {
        const key = item.dataset.productDetailsKey;
        if (!key || itemsByKey.has(key)) return false;

        itemsByKey.set(key, item);
        return true;
      });
      const orderedItems = uniqueItems.sort(
        (first, second) =>
          Number(first.dataset.productDetailsOrder || 0) - Number(second.dataset.productDetailsOrder || 0)
      );
      const mainItems = orderedItems.slice(0, MAIN_SPECIFICATION_LIMIT);
      const additionalItems = orderedItems.slice(MAIN_SPECIFICATION_LIMIT);

      this._content.replaceChildren(...mainItems);
      additionalItems.forEach((item) => this._additionalItems.append(item));

      this._additionalDetails.hidden = additionalItems.length === 0;
      if (additionalItems.length > 0) this._additionalDetails.open = this._additionalWasOpen;
      this._applyValueLengthClasses();
    }

    _applyValueLengthClasses(items = null) {
      const detailItems = items || [...this.querySelectorAll('[data-product-details-item]')];
      detailItems.forEach((item) => {
        const value = item.querySelector('.product-details__value');
        if (!value) return;

        const length = this._normalizeText(value.textContent).length;
        value.classList.toggle('product-details__value--long', length > 18);
        value.classList.toggle('product-details__value--xlong', length > 30);
      });
    }

    _collectValues() {
      const values = new Map();
      this.querySelectorAll('[data-product-details-item]').forEach((item) => {
        const key = item.dataset.productDetailsKey;
        const value = item.querySelector('.product-details__value');
        if (key && value) values.set(key, this._normalizeText(value.textContent));
      });
      return values;
    }

    _normalizeText(value) {
      return value.replace(/\s+/g, ' ').trim();
    }

    _highlightChangedValues(previousValues) {
      let hasHiddenChange = false;

      this.querySelectorAll('[data-product-details-item]').forEach((item) => {
        const key = item.dataset.productDetailsKey;
        const value = item.querySelector('.product-details__value');
        if (!key || !value || !previousValues.has(key)) return;
        if (previousValues.get(key) === this._normalizeText(value.textContent)) return;

        item.classList.add('product-details__item--updated');
        window.setTimeout(() => item.classList.remove('product-details__item--updated'), 1600);

        if (this._additionalDetails?.contains(item) && !this._additionalDetails.open) {
          hasHiddenChange = true;
        }
      });

      if (hasHiddenChange && this._additionalSummary) {
        this._additionalSummary.classList.add('product-details__accordion-summary--updated');
        window.setTimeout(
          () => this._additionalSummary?.classList.remove('product-details__accordion-summary--updated'),
          1600
        );
      }
    }

    _renderVariant(variantId, highlightChanges) {
      const normalizedVariantId = String(variantId);
      if (normalizedVariantId === this._currentVariantId) return;

      const template = this._variantTemplates.get(normalizedVariantId);
      if (!this._content || !template) return;

      const previousValues = highlightChanges ? this._collectValues() : null;
      this._closeTooltips();
      this._additionalItems?.replaceChildren();
      this._content.replaceChildren(template.content.cloneNode(true));
      this._currentVariantId = normalizedVariantId;
      this._organizeSpecifications();

      if (previousValues) {
        requestAnimationFrame(() => this._highlightChangedValues(previousValues));
      }
    }
  }

  customElements.define('product-details', ProductDetails);
}
